import {
  Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { ThemeProvider as StyledThemeProvider, useTheme } from 'styled-components';

import { Theme, ThemeOptions, getTheme } from '../../theme';
import { defaultSecondary } from '../../theme/variables';
import { getSufficientContrast } from '../../theme/helpers';

interface ThemeProviderProps {
  options?: ThemeOptions;
  children: ReactNode;
}

const ThemeProvider = ({ options: initialOptions = {}, children }: ThemeProviderProps) => {
  const [options, setOptions] = useState<ThemeOptions>(initialOptions);
  const theme = useMemo(() => getTheme(options), [options]);

  return (
    <StyledThemeProvider theme={theme}>
      <ThemeContext.Provider value={{ options, setOptions }}>
        {children}
      </ThemeContext.Provider>
    </StyledThemeProvider>
  );
};

interface ThemeContextValue {
  options?: ThemeOptions;
  setOptions?: Dispatch<SetStateAction<ThemeOptions>>;
}

export const ThemeContext = createContext<ThemeContextValue>({});

export const useBrandColor = (brandColor: string | null, active: boolean = true) => {
  const theme = useTheme() as Theme;

  const backgroundColor = theme.colors.gray[75];
  const effectiveBrandColor = brandColor ? getSufficientContrast(brandColor, backgroundColor, 2) : defaultSecondary;

  const { options, setOptions } = useContext(ThemeContext);

  useEffect(() => {
    if (active) {
      setOptions((options) => ({
        ...options,
        secondary: effectiveBrandColor,
      }));
    }
  }, [active, setOptions, effectiveBrandColor]);

  return options.secondary === effectiveBrandColor;
};

export default ThemeProvider;
